// ─── Laying out a day's blocks ───────────────────────────────────────────────
//
// Pure interval arithmetic, separated from the component because this is where
// the bugs are. A block placed one lane wrong, or given the wrong width,
// silently hides another booking behind it — and a hidden booking on a
// calendar is the same failure as a lost one.
//
// ── Why overlaps happen at all ──────────────────────────────────────────────
//
// Confirmed and blocked time cannot overlap: the database refuses it. But
// REQUESTED bookings deliberately do not reserve time (see @nulife/booking),
// so two people asking for the same Tuesday is a normal state, and the
// business needs to see both in order to choose. Any layout that assumed
// non-overlap would draw one on top of the other and quietly lose one.

export type Interval = { startsAt: Date; endsAt: Date };

export type Placed<T extends Interval> = T & {
  /** Which column within its overlapping cluster, from 0. */
  lane: number;
  /** How many columns the cluster needs, so a block knows its own width. */
  lanes: number;
};

/**
 * Assign each interval a lane so that no two overlapping items share one.
 *
 * Clusters first: items are grouped by transitive overlap, and lane count is
 * decided per cluster rather than per day. Otherwise one busy hour would make
 * every block on a quiet day thin, which wastes the space where the
 * information is.
 *
 * Ties are broken by start time, then by end time, then by id where present —
 * deterministic ordering matters because a calendar that reshuffles its
 * columns between renders is disorienting to read.
 */
export function assignLanes<T extends Interval & { id?: string }>(items: T[]): Placed<T>[] {
  const sorted = [...items].sort((a, b) =>
    a.startsAt.getTime() - b.startsAt.getTime() ||
    a.endsAt.getTime() - b.endsAt.getTime() ||
    String(a.id ?? "").localeCompare(String(b.id ?? "")));

  const out: Placed<T>[] = [];
  let cluster: Placed<T>[] = [];
  let clusterEnd = -Infinity;

  const closeCluster = () => {
    const lanes = cluster.reduce((n, c) => Math.max(n, c.lane + 1), 0);
    for (const c of cluster) out.push({ ...c, lanes });
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const item of sorted) {
    // A cluster ends when an item starts at or after everything before it has
    // finished. `>=` and not `>`: a 10:00–11:00 and an 11:00–12:00 do not
    // overlap, and drawing them side by side would waste half the width.
    if (item.startsAt.getTime() >= clusterEnd) closeCluster();

    const taken = new Set(
      cluster.filter(c => c.endsAt.getTime() > item.startsAt.getTime()).map(c => c.lane),
    );
    let lane = 0;
    while (taken.has(lane)) lane++;

    cluster.push({ ...item, lane, lanes: 0 });
    clusterEnd = Math.max(clusterEnd, item.endsAt.getTime());
  }
  closeCluster();

  return out.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime() || a.lane - b.lane);
}

/** Minutes from the start of the day, in local time. */
export function minutesIntoDay(when: Date): number {
  return when.getHours() * 60 + when.getMinutes();
}

/**
 * Clamp an interval to the grid's visible window.
 *
 * A booking that starts before the grid opens is still drawn, from the top,
 * rather than being positioned off-screen where it cannot be seen or clicked.
 * Losing a booking off the top of a calendar is exactly the class of quiet
 * failure this product is built to avoid.
 */
export function clampToWindow(
  item: Interval, windowStartMinute: number, windowEndMinute: number,
): { topMinute: number; heightMinutes: number } | null {
  const start = Math.max(minutesIntoDay(item.startsAt), windowStartMinute);
  const rawEnd = minutesIntoDay(item.endsAt);
  // An end of exactly midnight belongs to this day, not the next.
  const end = Math.min(rawEnd === 0 ? 24 * 60 : rawEnd, windowEndMinute);
  if (end <= start) return null;
  return { topMinute: start - windowStartMinute, heightMinutes: end - start };
}
